import { isPlatformBrowser } from '@angular/common';
import { Directive, Input, PLATFORM_ID, ElementRef, Renderer2, inject } from '@angular/core';
import type { OnChanges, SimpleChanges, AfterViewInit } from '@angular/core';

interface CropBounds {
  top: number;
  bottom: number;
}

@Directive({
  selector: '[appAutoCrop]',
  standalone: true,
})
export class AutoCropDirective implements OnChanges, AfterViewInit {
  @Input() public cropThreshold = 20;
  @Input() public cropWhiteBg = false;

  private currentSrc = '';
  private isProcessing = false;
  private readonly isBrowser: boolean;
  private readonly elementRef = inject(ElementRef<HTMLImageElement>);
  private readonly rendererService = inject(Renderer2);
  private readonly platformId = inject(PLATFORM_ID);

  constructor() {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['cropThreshold']) {
      this.setupCropping();
    }
  }

  public ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }
    this.setupCropping();
  }

  private setupCropping(): void {
    if (!this.isBrowser) {
      return;
    }

    const IMG = this.elementRef.nativeElement;
    const SRC = IMG.getAttribute('src') || IMG.src;

    if (this.isProcessing || this.currentSrc === SRC || !SRC || SRC.startsWith('data:')) {
      return;
    }

    this.currentSrc = SRC;
    this.isProcessing = true;

    const TEMP_IMG = new Image();
    TEMP_IMG.crossOrigin = 'anonymous';

    TEMP_IMG.onload = (): void => {
      this.cropImage(TEMP_IMG, IMG);
      this.isProcessing = false;
    };

    TEMP_IMG.onerror = (): void => {
      console.warn('Failed to load image with CORS:', SRC);
      // For same-origin or non-S3 images, retry without CORS header
      if (!SRC.includes('s3') && !SRC.includes('amazonaws')) {
        this.tryWithoutCORS(SRC, IMG);
      } else {
        this.isProcessing = false;
        console.error('S3 image failed - check CORS configuration on bucket');
      }
    };

    TEMP_IMG.src = SRC;
  }

  private tryWithoutCORS(src: string, targetImg: HTMLImageElement): void {
    if (!this.isBrowser) {
      return;
    }

    const TEMP_IMG = new Image();

    TEMP_IMG.onload = (): void => {
      this.cropImage(TEMP_IMG, targetImg);
      this.isProcessing = false;
    };

    TEMP_IMG.onerror = (): void => {
      this.isProcessing = false;
      console.error('Image failed to load without CORS:', src);
    };

    TEMP_IMG.src = src;
  }

  private cropImage(sourceImg: HTMLImageElement, targetImg: HTMLImageElement): void {
    if (!this.isBrowser) {
      return;
    }

    const CANVAS = document.createElement('canvas');
    const CTX = CANVAS.getContext('2d', { willReadFrequently: true });
    if (!CTX) {
      return;
    }

    try {
      CANVAS.width = sourceImg.naturalWidth || sourceImg.width;
      CANVAS.height = sourceImg.naturalHeight || sourceImg.height;
      CTX.drawImage(sourceImg, 0, 0);

      const IMAGE_DATA = CTX.getImageData(0, 0, CANVAS.width, CANVAS.height);
      const DATA = IMAGE_DATA.data;

      // eslint-disable-next-line @typescript-eslint/naming-convention
      const { top, bottom } = this.cropWhiteBg
        ? this.findWhiteBgBounds(DATA, CANVAS.width, CANVAS.height)
        : this.findTransparentBounds(DATA, CANVAS.width, CANVAS.height);

      const NEW_HEIGHT = bottom - top;
      if (NEW_HEIGHT > 50 && NEW_HEIGHT < CANVAS.height * 0.95) {
        const CROPPED_IMAGE = document.createElement('canvas');
        CROPPED_IMAGE.width = CANVAS.width;
        CROPPED_IMAGE.height = NEW_HEIGHT;

        const CROPPED_CTX = CROPPED_IMAGE.getContext('2d');
        if (!CROPPED_CTX) {
          return;
        }

        CROPPED_CTX.drawImage(
          CANVAS,
          0,
          top,
          CANVAS.width,
          NEW_HEIGHT,
          0,
          0,
          CANVAS.width,
          NEW_HEIGHT
        );

        const CROPPED_URL = CROPPED_IMAGE.toDataURL('image/png', 0.95);
        this.rendererService.setAttribute(targetImg, 'src', CROPPED_URL);
        this.currentSrc = CROPPED_URL;
      }
    } catch (error) {
      console.error('Unable to crop image:', error);
    }
  }

  private findTransparentBounds(
    data: Uint8ClampedArray,
    width: number,
    height: number
  ): CropBounds {
    let top = 0;
    let bottom = height;

    topLoop: for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const ALPHA = data[(y * width + x) * 4 + 3];
        if (ALPHA > this.cropThreshold) {
          top = Math.max(0, y - 2);
          break topLoop;
        }
      }
    }

    bottomLoop: for (let y = height - 1; y >= 0; y--) {
      for (let x = 0; x < width; x++) {
        const ALPHA = data[(y * width + x) * 4 + 3];
        if (ALPHA > this.cropThreshold) {
          bottom = Math.min(height, y + 3);
          break bottomLoop;
        }
      }
    }

    return { top, bottom };
  }

  private findWhiteBgBounds(data: Uint8ClampedArray, width: number, height: number): CropBounds {
    let top = 0;
    let bottom = height;
    const COLOR_THRESHOLD = 240;

    const IS_WHITE = (r: number, g: number, b: number): boolean =>
      r > COLOR_THRESHOLD && g > COLOR_THRESHOLD && b > COLOR_THRESHOLD;

    topLoop: for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        const IDX = (y * width + x) * 4;
        if (!IS_WHITE(data[IDX], data[IDX + 1], data[IDX + 2])) {
          top = Math.max(0, y - 2);
          break topLoop;
        }
      }
    }

    bottomLoop: for (let y = height - 1; y >= 0; y--) {
      for (let x = 0; x < width; x++) {
        const IDX = (y * width + x) * 4;
        if (!IS_WHITE(data[IDX], data[IDX + 1], data[IDX + 2])) {
          bottom = Math.min(height, y + 3);
          break bottomLoop;
        }
      }
    }

    return { top, bottom };
  }
}
